import React, { createContext, useContext, useState, useEffect } from "react";
import { parseDiscontinued } from "./utils";
import { isTstPlaceholder } from "./utils/imageHelpers";
import api, { fetchRoots, getCurrentAnnouncement } from "./api";

const API_BASE = "http://40.40.69.79:3051";

const DataContext = createContext();

export function DataProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rootCategories, setRootCategories] = useState([]);
  const [categoryHierarchy, setCategoryHierarchy] = useState({});
  const [navRoots, setNavRoots] = useState([]);
  const [announcements, setAnnouncements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("ZAR");
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch products (required)
        const productsRes = await api.get("/api/v1/seritonex/products/all", {
          responseType: "json",
        });

        // Get currency from products response headers
        const apiCurrency = productsRes.headers?.["x-currency"];
        if (apiCurrency) setCurrency(apiCurrency);

        const productsData = productsRes.data;

        // Fetch categories separately so a failure doesn't block products
        let categoriesData = null;
        try {
          const categoriesRes = await api.get("/api/v1/seritonex/category/all");
          categoriesData = categoriesRes.data;
        } catch (catErr) {
          console.warn("Categories fetch failed (non-fatal):", catErr);
        }

        // Fetch nav roots (non-fatal)
        try {
          const roots = await fetchRoots();
          const rootsArr = Array.isArray(roots) ? roots : Array.isArray(roots?.value) ? roots.value : [];
          if (!cancelled) setNavRoots(rootsArr);
        } catch (rootsErr) {
          console.warn("Nav roots fetch failed (non-fatal):", rootsErr);
        }

        // Fetch current announcement (non-fatal)
        try {
          const announcementData = await getCurrentAnnouncement();
          if (!cancelled) setAnnouncements(announcementData);
        } catch (annErr) {
          console.warn("Announcements fetch failed (non-fatal):", annErr);
        }

        if (!Array.isArray(productsData)) {
          throw new Error("Invalid products data format");
        }

        const normalizedProducts = productsData.map((d) => {
          const variants = Array.isArray(d.variants) ? d.variants : [];
          const totalStock = variants.reduce(
            (sum, v) => sum + (v.quantity_on_hand || 0),
            0,
          );

          let displayPrice = d.value_incl || d.value_excl || d.price || 0;
          if (variants.length > 0) {
            displayPrice =
              variants[0].value_incl ||
              variants[0].value_excl ||
              variants[0].price ||
              displayPrice;
          }

          let primaryImageUrl = "";

          if (
            d.primary_image?.path &&
            !isTstPlaceholder(d.primary_image.path)
          ) {
            primaryImageUrl = d.primary_image.path;
          } else if (
            variants.length > 0 &&
            variants[0].images?.length > 0 &&
            !isTstPlaceholder(variants[0].images[0].path)
          ) {
            primaryImageUrl = variants[0].images[0].path;
          } else if (
            variants.length > 0 &&
            variants[0].primary_image &&
            !isTstPlaceholder(variants[0].primary_image.path)
          ) {
            primaryImageUrl = variants[0].primary_image.path;
          }

          let secondaryImageUrl = null;
          if (
            variants.length > 0 &&
            variants[0].images?.length > 0 &&
            !isTstPlaceholder(variants[0].images[0].path)
          ) {
            secondaryImageUrl = variants[0].images[0].path;
          }

          return {
            id: d.product_id,
            title: d.title,
            name: d.title,
            price: displayPrice,
            valueExcl: d.value_excl || 0,
            valueIncl: d.value_incl || 0,
            description: d.description || "",
            brand: d.brand || "",
            image: primaryImageUrl,
            secondary_image: secondaryImageUrl,
            sku: d.sku || "",
            weight: parseFloat(d.weight) || 0,
            qty: totalStock,
            quantity: totalStock,
            quantity_on_hand: totalStock,
            category: d.category?.description || "",
            subCategory: d.sub_category?.description || "",
            categories: {
              main: d.category?.description || "",
              sub: d.sub_category?.description || "",
            },
            categoryId: d.category?.category_id,
            subCategoryId: d.sub_category?.category_id,
            category_id: d.category?.category_id,
            sub_category_id: d.sub_category?.category_id,
            main_category_id: d.category?.category_id,
            category_relationship_id: d.category?.category_relationship_id,
            classification_id: d.category?.classification_id,
            discontinued: parseDiscontinued(d.discontinued),
            primary_image: d.primary_image || null,
            images: Array.isArray(d.images) ? d.images : [],
            primaryImageUrl: primaryImageUrl,
            variations: variants,
          };
        });

        if (cancelled) return;

        setProducts(normalizedProducts);

        // Process categories to build hierarchy
        if (Array.isArray(categoriesData)) {
          // Extract unique category descriptions
          const categoryList = [];
          const categoryMap = new Map();

          categoriesData.forEach((cat) => {
            if (cat && cat.description && typeof cat.description === "string") {
              const desc = cat.description.trim();
              if (desc && !categoryList.includes(desc)) {
                categoryList.push(desc);
              }
              categoryMap.set(cat.category_id, cat);
            }
          });

          setCategories(categoryList.sort());

          // Build hierarchy (roots and children)
          const roots = categoriesData.filter(
            (cat) => !cat.parent_id || cat.parent_id === cat.category_id,
          );
          const hierarchyMap = {};

          roots.forEach((root) => {
            hierarchyMap[root.category_id] = categoriesData.filter(
              (cat) =>
                cat.parent_id === root.category_id &&
                cat.category_id !== root.category_id,
            );
          });

          setRootCategories(roots);
          setCategoryHierarchy(hierarchyMap);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to fetch data:", err);
        setError(err.message);

        // ╔══════════════════════════════════════════════════════════════╗
        // ║  TEMP TEST PRODUCTS FOR DEMO — remove before production     ║
        // ╚══════════════════════════════════════════════════════════════╝
        const TEST_PRODUCTS = [
          // CPUs — Intel (LGA1700)
          {
            id: "cpu-intel-i9-14900k",
            name: "Intel Core i9-14900K",
            brand: "Intel",
            price: 11499,
            qty: 5,
            image: "https://via.placeholder.com/300x200?text=i9-14900K",
            description:
              "24 cores (8P+16E), 32 threads, 6.0GHz boost, 253W TDP, LGA1700",
            categories: { main: "Components", sub: "CPU" },
          },
          {
            id: "cpu-intel-i7-14700k",
            name: "Intel Core i7-14700K",
            brand: "Intel",
            price: 7499,
            qty: 8,
            image: "https://via.placeholder.com/300x200?text=i7-14700K",
            description:
              "20 cores (8P+12E), 28 threads, 5.6GHz boost, 125W TDP, LGA1700",
            categories: { main: "Components", sub: "CPU" },
          },
          {
            id: "cpu-intel-i5-14600k",
            name: "Intel Core i5-14600K",
            brand: "Intel",
            price: 5299,
            qty: 12,
            image: "https://via.placeholder.com/300x200?text=i5-14600K",
            description:
              "14 cores (6P+8E), 20 threads, 5.3GHz boost, 125W TDP, LGA1700",
            categories: { main: "Components", sub: "CPU" },
          },
          {
            id: "cpu-intel-i5-14400f",
            name: "Intel Core i5-14400F",
            brand: "Intel",
            price: 3799,
            qty: 15,
            image: "https://via.placeholder.com/300x200?text=i5-14400F",
            description:
              "10 cores (6P+4E), 16 threads, 4.7GHz boost, 65W TDP, LGA1700, No iGPU",
            categories: { main: "Components", sub: "CPU" },
          },
          {
            id: "cpu-intel-i3-14100f",
            name: "Intel Core i3-14100F",
            brand: "Intel",
            price: 2199,
            qty: 20,
            image: "https://via.placeholder.com/300x200?text=i3-14100F",
            description:
              "4 cores, 8 threads, 4.7GHz boost, 58W TDP, LGA1700, No iGPU",
            categories: { main: "Components", sub: "CPU" },
          },
          // CPUs — AMD (AM5)
          {
            id: "cpu-amd-9950x3d",
            name: "AMD Ryzen 9 9950X3D",
            brand: "AMD",
            price: 13499,
            qty: 3,
            image: "https://via.placeholder.com/300x200?text=9950X3D",
            description:
              "16 cores, 32 threads, 5.7GHz boost, 170W TDP, AM5, 3D V-Cache",
            categories: { main: "Components", sub: "CPU" },
          },
          {
            id: "cpu-amd-9800x3d",
            name: "AMD Ryzen 7 9800X3D",
            brand: "AMD",
            price: 8999,
            qty: 4,
            image: "https://via.placeholder.com/300x200?text=9800X3D",
            description:
              "8 cores, 16 threads, 5.2GHz boost, 120W TDP, AM5, 3D V-Cache",
            categories: { main: "Components", sub: "CPU" },
          },
          {
            id: "cpu-amd-7800x3d",
            name: "AMD Ryzen 7 7800X3D",
            brand: "AMD",
            price: 7299,
            qty: 6,
            image: "https://via.placeholder.com/300x200?text=7800X3D",
            description:
              "8 cores, 16 threads, 5.0GHz boost, 120W TDP, AM5, 3D V-Cache",
            categories: { main: "Components", sub: "CPU" },
          },
          {
            id: "cpu-amd-7600x",
            name: "AMD Ryzen 5 7600X",
            brand: "AMD",
            price: 4299,
            qty: 10,
            image: "https://via.placeholder.com/300x200?text=7600X",
            description: "6 cores, 12 threads, 5.3GHz boost, 105W TDP, AM5",
            categories: { main: "Components", sub: "CPU" },
          },
          {
            id: "cpu-amd-7600",
            name: "AMD Ryzen 5 7600",
            brand: "AMD",
            price: 3599,
            qty: 14,
            image: "https://via.placeholder.com/300x200?text=7600",
            description: "6 cores, 12 threads, 5.1GHz boost, 65W TDP, AM5",
            categories: { main: "Components", sub: "CPU" },
          },
          // CPUs — AMD (AM4) — budget
          {
            id: "cpu-amd-5600",
            name: "AMD Ryzen 5 5600",
            brand: "AMD",
            price: 2499,
            qty: 18,
            image: "https://via.placeholder.com/300x200?text=5600",
            description: "6 cores, 12 threads, 4.4GHz boost, 65W TDP, AM4",
            categories: { main: "Components", sub: "CPU" },
          },
          {
            id: "cpu-amd-5700x3d",
            name: "AMD Ryzen 7 5700X3D",
            brand: "AMD",
            price: 4499,
            qty: 7,
            image: "https://via.placeholder.com/300x200?text=5700X3D",
            description:
              "8 cores, 16 threads, 4.1GHz boost, 105W TDP, AM4, 3D V-Cache",
            categories: { main: "Components", sub: "CPU" },
          },
          // Motherboards — Intel LGA1700
          {
            id: "mobo-asus-z790-a",
            name: "ASUS ROG STRIX Z790-A Gaming WiFi DDR5",
            brand: "ASUS",
            price: 6499,
            qty: 6,
            image: "https://via.placeholder.com/300x200?text=Z790-A",
            description: "LGA1700, ATX, DDR5, PCIe 5.0, WiFi 6E, 4x M.2",
            categories: { main: "Components", sub: "Motherboard" },
          },
          {
            id: "mobo-msi-z790-edge",
            name: "MSI MPG Z790 Edge WiFi DDR5",
            brand: "MSI",
            price: 5999,
            qty: 5,
            image: "https://via.placeholder.com/300x200?text=Z790-Edge",
            description: "LGA1700, ATX, DDR5, PCIe 5.0, WiFi 6E, 4x M.2",
            categories: { main: "Components", sub: "Motherboard" },
          },
          {
            id: "mobo-giga-b760-aorus",
            name: "Gigabyte B760 AORUS Elite AX DDR5",
            brand: "Gigabyte",
            price: 3999,
            qty: 9,
            image: "https://via.placeholder.com/300x200?text=B760-Aorus",
            description: "LGA1700, ATX, DDR5, WiFi 6E, 2x M.2",
            categories: { main: "Components", sub: "Motherboard" },
          },
          {
            id: "mobo-msi-b760-mortar",
            name: "MSI MAG B760M Mortar WiFi DDR4",
            brand: "MSI",
            price: 3199,
            qty: 11,
            image: "https://via.placeholder.com/300x200?text=B760M",
            description: "LGA1700, Micro-ATX, DDR4, WiFi 6E, 2x M.2",
            categories: { main: "Components", sub: "Motherboard" },
          },
          // Motherboards — AMD AM5
          {
            id: "mobo-asus-x670e-hero",
            name: "ASUS ROG Crosshair X670E Hero DDR5",
            brand: "ASUS",
            price: 9999,
            qty: 3,
            image: "https://via.placeholder.com/300x200?text=X670E-Hero",
            description: "AM5, ATX, DDR5, PCIe 5.0, WiFi 6E, 4x M.2",
            categories: { main: "Components", sub: "Motherboard" },
          },
          {
            id: "mobo-msi-b650-tomahawk",
            name: "MSI MAG B650 TOMAHAWK WiFi DDR5",
            brand: "MSI",
            price: 4599,
            qty: 7,
            image: "https://via.placeholder.com/300x200?text=B650-Tomahawk",
            description: "AM5, ATX, DDR5, WiFi 6E, 2x M.2",
            categories: { main: "Components", sub: "Motherboard" },
          },
          {
            id: "mobo-giga-b650-eagle",
            name: "Gigabyte B650 Eagle AX DDR5",
            brand: "Gigabyte",
            price: 3499,
            qty: 8,
            image: "https://via.placeholder.com/300x200?text=B650-Eagle",
            description: "AM5, ATX, DDR5, WiFi 6, 2x M.2",
            categories: { main: "Components", sub: "Motherboard" },
          },
          // Motherboards — AMD AM4
          {
            id: "mobo-msi-b550-gaming",
            name: "MSI MAG B550 TOMAHAWK DDR4",
            brand: "MSI",
            price: 2799,
            qty: 10,
            image: "https://via.placeholder.com/300x200?text=B550-Tomahawk",
            description: "AM4, ATX, DDR4, 2x M.2",
            categories: { main: "Components", sub: "Motherboard" },
          },
          {
            id: "mobo-asus-b550-tuf",
            name: "ASUS TUF Gaming B550-PLUS WiFi DDR4",
            brand: "ASUS",
            price: 2999,
            qty: 8,
            image: "https://via.placeholder.com/300x200?text=B550-TUF",
            description: "AM4, ATX, DDR4, WiFi 6, 2x M.2",
            categories: { main: "Components", sub: "Motherboard" },
          },
          // RAM — DDR5
          {
            id: "ram-corsair-ddr5-6000-32",
            name: "Corsair Vengeance 32GB (2x16GB) DDR5-6000",
            brand: "Corsair",
            price: 2199,
            qty: 20,
            image: "https://via.placeholder.com/300x200?text=Corsair-DDR5-32",
            description: "DDR5 6000MHz, CL30, 2x16GB Kit, Intel XMP 3.0",
            categories: { main: "Components", sub: "RAM" },
          },
          {
            id: "ram-corsair-ddr5-6000-64",
            name: "Corsair Vengeance 64GB (2x32GB) DDR5-6000",
            brand: "Corsair",
            price: 4299,
            qty: 8,
            image: "https://via.placeholder.com/300x200?text=Corsair-DDR5-64",
            description: "DDR5 6000MHz, CL30, 2x32GB Kit",
            categories: { main: "Components", sub: "RAM" },
          },
          {
            id: "ram-gskill-ddr5-6400-32",
            name: "G.Skill Trident Z5 RGB 32GB (2x16GB) DDR5-6400",
            brand: "G.Skill",
            price: 2899,
            qty: 12,
            image: "https://via.placeholder.com/300x200?text=GSkill-DDR5-32",
            description: "DDR5 6400MHz, CL32, 2x16GB, RGB",
            categories: { main: "Components", sub: "RAM" },
          },
          {
            id: "ram-kingston-ddr5-5600-32",
            name: "Kingston Fury Beast 32GB (2x16GB) DDR5-5600",
            brand: "Kingston",
            price: 1799,
            qty: 15,
            image: "https://via.placeholder.com/300x200?text=Kingston-DDR5-32",
            description: "DDR5 5600MHz, CL36, 2x16GB",
            categories: { main: "Components", sub: "RAM" },
          },
          // RAM — DDR4
          {
            id: "ram-corsair-ddr4-3200-16",
            name: "Corsair Vengeance LPX 16GB (2x8GB) DDR4-3200",
            brand: "Corsair",
            price: 699,
            qty: 25,
            image: "https://via.placeholder.com/300x200?text=Corsair-DDR4-16",
            description: "DDR4 3200MHz, CL16, 2x8GB Kit",
            categories: { main: "Components", sub: "RAM" },
          },
          {
            id: "ram-corsair-ddr4-3600-32",
            name: "Corsair Vengeance LPX 32GB (2x16GB) DDR4-3600",
            brand: "Corsair",
            price: 1399,
            qty: 18,
            image: "https://via.placeholder.com/300x200?text=Corsair-DDR4-32",
            description: "DDR4 3600MHz, CL18, 2x16GB Kit",
            categories: { main: "Components", sub: "RAM" },
          },
          {
            id: "ram-gskill-ddr4-3600-32",
            name: "G.Skill Ripjaws V 32GB (2x16GB) DDR4-3600",
            brand: "G.Skill",
            price: 1499,
            qty: 14,
            image: "https://via.placeholder.com/300x200?text=GSkill-DDR4-32",
            description: "DDR4 3600MHz, CL16, 2x16GB Kit",
            categories: { main: "Components", sub: "RAM" },
          },
          // GPUs — NVIDIA
          {
            id: "gpu-rtx-5090",
            name: "NVIDIA GeForce RTX 5090 Founders Edition",
            brand: "NVIDIA",
            price: 42999,
            qty: 2,
            image: "https://via.placeholder.com/300x200?text=RTX-5090",
            description: "32GB GDDR7, 575W TDP, DLSS 4, 21760 CUDA cores",
            categories: { main: "Components", sub: "GPU" },
          },
          {
            id: "gpu-rtx-5080",
            name: "NVIDIA GeForce RTX 5080 Founders Edition",
            brand: "NVIDIA",
            price: 24999,
            qty: 4,
            image: "https://via.placeholder.com/300x200?text=RTX-5080",
            description: "16GB GDDR7, 360W TDP, DLSS 4, 10752 CUDA cores",
            categories: { main: "Components", sub: "GPU" },
          },
          {
            id: "gpu-rtx-5070ti",
            name: "NVIDIA GeForce RTX 5070 Ti",
            brand: "NVIDIA",
            price: 17999,
            qty: 5,
            image: "https://via.placeholder.com/300x200?text=RTX-5070Ti",
            description: "16GB GDDR7, 300W TDP, DLSS 4",
            categories: { main: "Components", sub: "GPU" },
          },
          {
            id: "gpu-rtx-5070",
            name: "NVIDIA GeForce RTX 5070",
            brand: "NVIDIA",
            price: 12999,
            qty: 7,
            image: "https://via.placeholder.com/300x200?text=RTX-5070",
            description: "12GB GDDR7, 250W TDP, DLSS 4",
            categories: { main: "Components", sub: "GPU" },
          },
          {
            id: "gpu-rtx-4070ti-super",
            name: "NVIDIA GeForce RTX 4070 Ti SUPER",
            brand: "NVIDIA",
            price: 14999,
            qty: 6,
            image: "https://via.placeholder.com/300x200?text=RTX-4070TiS",
            description: "16GB GDDR6X, 285W TDP, DLSS 3",
            categories: { main: "Components", sub: "GPU" },
          },
          {
            id: "gpu-rtx-4060ti",
            name: "NVIDIA GeForce RTX 4060 Ti 8GB",
            brand: "NVIDIA",
            price: 8499,
            qty: 10,
            image: "https://via.placeholder.com/300x200?text=RTX-4060Ti",
            description: "8GB GDDR6, 160W TDP, DLSS 3",
            categories: { main: "Components", sub: "GPU" },
          },
          {
            id: "gpu-rtx-4060",
            name: "NVIDIA GeForce RTX 4060 8GB",
            brand: "NVIDIA",
            price: 5999,
            qty: 14,
            image: "https://via.placeholder.com/300x200?text=RTX-4060",
            description: "8GB GDDR6, 115W TDP, DLSS 3",
            categories: { main: "Components", sub: "GPU" },
          },
          // GPUs — AMD
          {
            id: "gpu-rx-9070xt",
            name: "AMD Radeon RX 9070 XT",
            brand: "AMD",
            price: 13999,
            qty: 5,
            image: "https://via.placeholder.com/300x200?text=RX-9070XT",
            description: "16GB GDDR6, 300W TDP, RDNA 4, FSR 4",
            categories: { main: "Components", sub: "GPU" },
          },
          {
            id: "gpu-rx-7800xt",
            name: "AMD Radeon RX 7800 XT",
            brand: "AMD",
            price: 9499,
            qty: 8,
            image: "https://via.placeholder.com/300x200?text=RX-7800XT",
            description: "16GB GDDR6, 263W TDP, RDNA 3",
            categories: { main: "Components", sub: "GPU" },
          },
          {
            id: "gpu-rx-7600",
            name: "AMD Radeon RX 7600 8GB",
            brand: "AMD",
            price: 4999,
            qty: 12,
            image: "https://via.placeholder.com/300x200?text=RX-7600",
            description: "8GB GDDR6, 150W TDP, RDNA 3",
            categories: { main: "Components", sub: "GPU" },
          },
          // Storage — M.2 NVMe
          {
            id: "stor-samsung-990pro-2tb",
            name: "Samsung 990 Pro 2TB M.2 NVMe PCIe 4.0",
            brand: "Samsung",
            price: 3499,
            qty: 15,
            image: "https://via.placeholder.com/300x200?text=990Pro-2TB",
            description: "7450/6900 MB/s read/write, PCIe 4.0",
            categories: { main: "Components", sub: "Storage" },
          },
          {
            id: "stor-samsung-990pro-1tb",
            name: "Samsung 990 Pro 1TB M.2 NVMe PCIe 4.0",
            brand: "Samsung",
            price: 2199,
            qty: 20,
            image: "https://via.placeholder.com/300x200?text=990Pro-1TB",
            description: "7450/6900 MB/s read/write, PCIe 4.0",
            categories: { main: "Components", sub: "Storage" },
          },
          {
            id: "stor-wd-sn850x-2tb",
            name: "WD Black SN850X 2TB M.2 NVMe PCIe 4.0",
            brand: "Western Digital",
            price: 2999,
            qty: 12,
            image: "https://via.placeholder.com/300x200?text=SN850X-2TB",
            description: "7300/6600 MB/s read/write, PCIe 4.0",
            categories: { main: "Components", sub: "Storage" },
          },
          {
            id: "stor-crucial-t700-2tb",
            name: "Crucial T700 2TB M.2 NVMe PCIe 5.0",
            brand: "Crucial",
            price: 4999,
            qty: 6,
            image: "https://via.placeholder.com/300x200?text=T700-2TB",
            description: "12400/11800 MB/s read/write, PCIe 5.0",
            categories: { main: "Components", sub: "Storage" },
          },
          // Storage — SATA SSD
          {
            id: "stor-samsung-870evo-1tb",
            name: "Samsung 870 EVO 1TB SATA SSD",
            brand: "Samsung",
            price: 1499,
            qty: 22,
            image: "https://via.placeholder.com/300x200?text=870EVO-1TB",
            description: '560/530 MB/s read/write, 2.5" SATA III',
            categories: { main: "Components", sub: "Storage" },
          },
          {
            id: "stor-crucial-mx500-1tb",
            name: "Crucial MX500 1TB SATA SSD",
            brand: "Crucial",
            price: 1199,
            qty: 18,
            image: "https://via.placeholder.com/300x200?text=MX500-1TB",
            description: '560/510 MB/s read/write, 2.5" SATA III',
            categories: { main: "Components", sub: "Storage" },
          },
          // PSUs
          {
            id: "psu-corsair-rm1000x",
            name: "Corsair RM1000x 1000W 80+ Gold ATX 3.0",
            brand: "Corsair",
            price: 3999,
            qty: 6,
            image: "https://via.placeholder.com/300x200?text=RM1000x",
            description:
              "1000W, 80+ Gold, Fully Modular, ATX 3.0, PCIe 5.0 cable",
            categories: { main: "Components", sub: "PSU" },
          },
          {
            id: "psu-corsair-rm850x",
            name: "Corsair RM850x 850W 80+ Gold",
            brand: "Corsair",
            price: 2699,
            qty: 9,
            image: "https://via.placeholder.com/300x200?text=RM850x",
            description: "850W, 80+ Gold, Fully Modular",
            categories: { main: "Components", sub: "PSU" },
          },
          {
            id: "psu-seasonic-focus-750",
            name: "Seasonic Focus GX-750 750W 80+ Gold",
            brand: "Seasonic",
            price: 2199,
            qty: 11,
            image: "https://via.placeholder.com/300x200?text=Focus-750",
            description: "750W, 80+ Gold, Fully Modular",
            categories: { main: "Components", sub: "PSU" },
          },
          {
            id: "psu-evga-700br",
            name: "EVGA 700 BR 700W 80+ Bronze",
            brand: "EVGA",
            price: 1299,
            qty: 15,
            image: "https://via.placeholder.com/300x200?text=EVGA-700BR",
            description: "700W, 80+ Bronze, Non-Modular",
            categories: { main: "Components", sub: "PSU" },
          },
          {
            id: "psu-corsair-rm550x",
            name: "Corsair RM550x 550W 80+ Gold",
            brand: "Corsair",
            price: 1499,
            qty: 13,
            image: "https://via.placeholder.com/300x200?text=RM550x",
            description: "550W, 80+ Gold, Fully Modular",
            categories: { main: "Components", sub: "PSU" },
          },
          {
            id: "psu-msi-a1000g",
            name: "MSI MEG Ai1000P 1000W 80+ Platinum ATX 3.0",
            brand: "MSI",
            price: 5499,
            qty: 4,
            image: "https://via.placeholder.com/300x200?text=MEG-1000P",
            description: "1000W, 80+ Platinum, Fully Modular, ATX 3.0",
            categories: { main: "Components", sub: "PSU" },
          },
          // Cases
          {
            id: "case-nzxt-h7-flow",
            name: "NZXT H7 Flow ATX Mid Tower",
            brand: "NZXT",
            price: 2499,
            qty: 7,
            image: "https://via.placeholder.com/300x200?text=H7-Flow",
            description:
              "ATX Mid Tower, 400mm GPU, 185mm cooler clearance, mesh front",
            categories: { main: "Components", sub: "Case" },
          },
          {
            id: "case-lianli-o11d",
            name: "Lian Li O11 Dynamic EVO ATX Mid Tower",
            brand: "Lian Li",
            price: 3299,
            qty: 5,
            image: "https://via.placeholder.com/300x200?text=O11D-Evo",
            description:
              "ATX Mid Tower, 422mm GPU, 167mm cooler clearance, dual chamber",
            categories: { main: "Components", sub: "Case" },
          },
          {
            id: "case-corsair-4000d",
            name: "Corsair 4000D Airflow ATX Mid Tower",
            brand: "Corsair",
            price: 1899,
            qty: 12,
            image: "https://via.placeholder.com/300x200?text=4000D",
            description: "ATX Mid Tower, 360mm GPU, 170mm cooler clearance",
            categories: { main: "Components", sub: "Case" },
          },
          {
            id: "case-fractal-north",
            name: "Fractal Design North ATX Mid Tower",
            brand: "Fractal Design",
            price: 2999,
            qty: 6,
            image: "https://via.placeholder.com/300x200?text=FD-North",
            description:
              "ATX Mid Tower, 355mm GPU, 170mm cooler clearance, wood panel",
            categories: { main: "Components", sub: "Case" },
          },
          {
            id: "case-coolermaster-nr200",
            name: "Cooler Master NR200P Mini-ITX Case",
            brand: "Cooler Master",
            price: 1699,
            qty: 8,
            image: "https://via.placeholder.com/300x200?text=NR200P",
            description: "Mini-ITX SFF, 330mm GPU, 155mm cooler clearance",
            categories: { main: "Components", sub: "Case" },
          },
          {
            id: "case-corsair-3000d",
            name: "Corsair 3000D Airflow Micro-ATX Mid Tower",
            brand: "Corsair",
            price: 1399,
            qty: 10,
            image: "https://via.placeholder.com/300x200?text=3000D",
            description:
              "Micro-ATX Mid Tower, 340mm GPU, 165mm cooler clearance",
            categories: { main: "Components", sub: "Case" },
          },
          // CPU Coolers — Air
          {
            id: "cool-noctua-nhd15",
            name: "Noctua NH-D15 chromax.black",
            brand: "Noctua",
            price: 1899,
            qty: 9,
            image: "https://via.placeholder.com/300x200?text=NH-D15",
            description:
              "Dual tower, 165mm height, 250W+ TDP rating, AM4/AM5/LGA1700",
            categories: { main: "Components", sub: "Cooling" },
          },
          {
            id: "cool-deepcool-ak620",
            name: "DeepCool AK620 Dual Tower",
            brand: "DeepCool",
            price: 1199,
            qty: 14,
            image: "https://via.placeholder.com/300x200?text=AK620",
            description:
              "Dual tower, 160mm height, 260W TDP rating, AM4/AM5/LGA1700",
            categories: { main: "Components", sub: "Cooling" },
          },
          {
            id: "cool-coolermaster-212",
            name: "Cooler Master Hyper 212 EVO V2",
            brand: "Cooler Master",
            price: 699,
            qty: 20,
            image: "https://via.placeholder.com/300x200?text=Hyper-212",
            description:
              "Single tower, 159mm height, 150W TDP, AM4/AM5/LGA1700",
            categories: { main: "Components", sub: "Cooling" },
          },
          {
            id: "cool-thermalright-pa120",
            name: "Thermalright Peerless Assassin 120 SE",
            brand: "Thermalright",
            price: 599,
            qty: 16,
            image: "https://via.placeholder.com/300x200?text=PA-120SE",
            description: "Dual tower, 155mm height, 260W TDP, AM4/AM5/LGA1700",
            categories: { main: "Components", sub: "Cooling" },
          },
          // CPU Coolers — AIO Liquid
          {
            id: "cool-corsair-h150i",
            name: "Corsair iCUE H150i Elite LCD XT 360mm AIO",
            brand: "Corsair",
            price: 4999,
            qty: 5,
            image: "https://via.placeholder.com/300x200?text=H150i-LCD",
            description: "360mm AIO, LCD display, AM4/AM5/LGA1700",
            categories: { main: "Components", sub: "Cooling" },
          },
          {
            id: "cool-nzxt-kraken-360",
            name: "NZXT Kraken 360 RGB AIO",
            brand: "NZXT",
            price: 4499,
            qty: 6,
            image: "https://via.placeholder.com/300x200?text=Kraken-360",
            description: "360mm AIO, LCD screen, AM4/AM5/LGA1700",
            categories: { main: "Components", sub: "Cooling" },
          },
          {
            id: "cool-arctic-lf2-240",
            name: "Arctic Liquid Freezer II 240mm AIO",
            brand: "Arctic",
            price: 1999,
            qty: 10,
            image: "https://via.placeholder.com/300x200?text=LF2-240",
            description: "240mm AIO, AM4/AM5/LGA1700, integrated VRM fan",
            categories: { main: "Components", sub: "Cooling" },
          },
        ];

        setProducts(TEST_PRODUCTS);
        setCategories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAllData();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = {
    products,
    categories,
    rootCategories,
    categoryHierarchy,
    navRoots,
    announcements,
    loading,
    currency,
    error,
    setProducts, // Allow updates for inventory changes
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};
